import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  HostListener,
  computed,
  contentChild,
  forwardRef,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { CadsFieldComponent } from '../cads-field/cads-field.component';
import { CadsFieldCaptionSlotDirective } from '../cads-field/cads-field-caption-slot.directive';
import type { CadsFieldConfig } from '../cads-field/cads-field.config';
import { nextCadsFormKitId } from '../internal/cads-form-kit-ids';

import { CadsSelectOptionsSlotDirective } from './cads-select-options-slot.directive';
import type { CadsSelectConfig } from './cads-select.config';

/**
 * Single-select dropdown for reactive forms. Implements {@link ControlValueAccessor}; value is `string | null`
 * (option `value`, or `null` in the placeholder state). Renders a trigger + animated inline panel inside
 * {@link CadsFieldComponent} label and caption with `bare: true` so the trigger carries the bordered card; the option
 * panel sits outside that border. The listbox (default rows or projected options) is rendered only while the panel
 * is open so option controls are not in the tab order when collapsed. On the trigger, **Enter** / **Space** toggle the
 * panel (with `preventDefault` so the button does not double-fire); **ArrowDown** opens when closed and moves focus into
 * the list; **ArrowUp** / **ArrowDown** / **Home** / **End** move among default options while open; **Escape** closes and
 * returns focus to the trigger.
 * Optional `[cadsSelectOptions]` projection
 * replaces the default option list. Option
 * {@link CadsSelectOption.value} must be non-empty — `''` is reserved for the internal placeholder state.
 */
@Component({
    selector: 'cads-select',
    imports: [CadsFieldComponent],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CadsSelectComponent),
            multi: true,
        },
    ],
    templateUrl: './cads-select.component.html',
    styleUrl: './cads-select.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        class: 'cads-select-host',
    }
})
export class CadsSelectComponent implements ControlValueAccessor {
  readonly config = input.required<CadsSelectConfig>();

  private readonly _host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** When set, default {@link CadsSelectConfig.options} rows are hidden in favor of projected `[cadsSelectOptions]`. */
  @ContentChild(CadsSelectOptionsSlotDirective)
  protected _customOptionsSlot?: CadsSelectOptionsSlotDirective;

  /**
   * Options after validating {@link CadsSelectOption.value} is never `''` (reserved for the placeholder state).
   */
  protected readonly _optionsForTemplate = computed(() => {
    const opts = this.config().options;
    for (const opt of opts) {
      if (opt.value === '') {
        throw new Error(
          'cads-select: CadsSelectOption.value must not be an empty string; "" is reserved for the placeholder state.',
        );
      }
    }
    return opts;
  });

  private readonly _fallbackInputId = nextCadsFormKitId('cads-select');

  protected readonly _value = signal<string>('');
  protected readonly _panelOpen = signal(false);
  protected readonly _controlDisabled = signal(false);

  protected readonly _inputId = computed(() => {
    const fromConfig = this.config().inputId?.trim();
    return fromConfig || this._fallbackInputId;
  });

  protected readonly _listboxId = computed(() => `${this._inputId()}-listbox`);

  protected readonly _disabled = computed(() => this._controlDisabled() || this.config().disabled === true);

  private readonly _captionSlot = contentChild(CadsFieldCaptionSlotDirective);

  protected readonly _hasProjectedCaption = computed(() => this._captionSlot() !== undefined);

  protected readonly _hasError = computed(() => (this.config().errorMessage?.trim() ?? '').length > 0);

  protected readonly _captionId = computed(() => `${this._inputId()}-caption`);

  protected readonly _showCaption = computed(() => {
    if (this._hasError()) {
      return true;
    }
    if (this._hasProjectedCaption()) {
      return true;
    }
    return (this.config().helperText?.trim() ?? '').length > 0;
  });

  protected readonly _placeholder = computed(() => this.config().placeholder ?? 'Select…');

  protected readonly _isPlaceholderActive = computed(() => this._value() === '');

  protected readonly _selectedLabel = computed(() => {
    const v = this._value();
    if (v === '') {
      return '';
    }
    const fromOption = this.config().options.find((o) => o.value === v)?.label;
    if (fromOption !== undefined) {
      return fromOption;
    }
    const fromMap = this.config().selectedValueLabels?.[v];
    if (fromMap !== undefined) {
      return fromMap;
    }
    return v;
  });

  protected readonly _fieldConfig = computed((): CadsFieldConfig => {
    void this._optionsForTemplate();
    return {
      label: this.config().label,
      controlId: this._inputId(),
      helperText: this.config().helperText,
      errorMessage: this.config().errorMessage,
      required: this.config().required === true,
      bare: true,
    };
  });

  /** Blue “open” trigger chrome while the panel is expanded (suppressed when invalid or disabled). */
  protected readonly _panelOpenChrome = computed(
    () => this._panelOpen() && !this._hasError() && !this._disabled(),
  );

  /** Chevron “open” rotation when expanded and error chrome is not suppressing the open affordance. */
  protected readonly _chevronOpen = computed(() => this._panelOpen() && !this._hasError());

  private _onChange: (value: string | null) => void = () => undefined;
  private _onTouched: () => void = () => undefined;

  writeValue(value: string | null | undefined): void {
    this._value.set(value ?? '');
    this._panelOpen.set(false);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._controlDisabled.set(isDisabled);
    if (isDisabled) {
      this._panelOpen.set(false);
    }
  }

  /**
   * Selects an option by `value`, emits the CVA change, closes the panel, and marks touched. Intended for projected
   * `[cadsSelectOptions]` UIs (call via a template ref on `cads-select`).
   */
  selectOptionValue(value: string): void {
    if (value === '') {
      throw new Error('cads-select: selectOptionValue does not accept ""; "" is reserved for the placeholder state.');
    }
    const wasOpen = this._panelOpen();
    this._value.set(value);
    this._onChange(value);
    this._panelOpen.set(false);
    this._onTouched();
    if (wasOpen) {
      setTimeout(() => this._focusTrigger());
    }
  }

  protected _togglePanel(): void {
    if (this._disabled()) {
      return;
    }
    this._panelOpen.update((o) => !o);
  }

  protected _closePanel(): void {
    this._panelOpen.set(false);
  }

  protected _onTriggerBlur(): void {
    this._onTouched();
  }

  protected _onDefaultOptionPick(value: string): void {
    this.selectOptionValue(value);
  }

  protected _onTriggerKeydown(event: KeyboardEvent): void {
    if (this._disabled()) {
      return;
    }
    if (event.key === 'Escape' && this._panelOpen()) {
      event.preventDefault();
      this._closePanel();
      this._focusTrigger();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this._panelOpen()) {
        this._panelOpen.set(true);
        setTimeout(() => this._focusInitialOption());
      } else {
        this._focusInitialOption();
      }
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this._panelOpen()) {
        this._panelOpen.set(true);
        setTimeout(() => this._focusLastEnabledOption());
      } else {
        this._focusLastEnabledOption();
      }
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this._panelOpen.update((open) => !open);
    }
  }

  protected _onOptionKeydown(event: KeyboardEvent): void {
    if (this._disabled()) {
      return;
    }
    const target = event.currentTarget as HTMLButtonElement;
    if (event.key === 'Escape') {
      event.preventDefault();
      this._closePanel();
      this._focusTrigger();
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this._focusAdjacentEnabledOption(target, 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      this._focusAdjacentEnabledOption(target, -1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      this._focusFirstEnabledOption();
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      this._focusLastEnabledOption();
    }
  }

  @HostListener('focusout', ['$event'])
  protected _onFocusOut(event: FocusEvent): void {
    if (!this._panelOpen()) {
      return;
    }
    const next = event.relatedTarget as Node | null;
    if (next && this._host.nativeElement.contains(next)) {
      return;
    }
    this._closePanel();
  }

  @HostListener('document:pointerdown', ['$event'])
  protected _onDocumentPointerdown(event: PointerEvent): void {
    if (!this._panelOpen()) {
      return;
    }
    const t = event.target as Node | null;
    if (t && this._host.nativeElement.contains(t)) {
      return;
    }
    this._closePanel();
  }

  private _defaultOptionButtons(): HTMLButtonElement[] {
    return Array.from(this._host.nativeElement.querySelectorAll<HTMLButtonElement>('.cads-select__option'));
  }

  private _enabledDefaultOptionButtons(): HTMLButtonElement[] {
    return this._defaultOptionButtons().filter((b) => !b.disabled);
  }

  private _focusTrigger(): void {
    const el = document.getElementById(this._inputId()) as HTMLButtonElement | null;
    el?.focus();
  }

  private _focusInitialOption(): void {
    if (this._customOptionsSlot) {
      return;
    }
    const enabled = this._enabledDefaultOptionButtons();
    if (enabled.length === 0) {
      return;
    }
    const v = this._value();
    if (v !== '') {
      const match = enabled.find((b) => b.dataset['optionValue'] === v);
      (match ?? enabled[0]).focus();
      return;
    }
    enabled[0].focus();
  }

  private _focusFirstEnabledOption(): void {
    const enabled = this._enabledDefaultOptionButtons();
    enabled[0]?.focus();
  }

  private _focusLastEnabledOption(): void {
    const enabled = this._enabledDefaultOptionButtons();
    enabled[enabled.length - 1]?.focus();
  }

  private _focusAdjacentEnabledOption(current: HTMLButtonElement, delta: 1 | -1): void {
    const enabled = this._enabledDefaultOptionButtons();
    if (enabled.length === 0) {
      return;
    }
    let idx = enabled.indexOf(current);
    if (idx < 0) {
      enabled[0].focus();
      return;
    }
    idx = (idx + delta + enabled.length) % enabled.length;
    enabled[idx].focus();
  }
}
